import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from './firebase';
import { useAuth } from './AuthContext';
import './UserManagement.css';

function UserManagement({ onClose }) {
  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('vendedor');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user: currentUser } = useAuth();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setError('');

    if (!newUserEmail || !newUserPassword) {
      setError('Por favor completa todos los campos');
      return;
    }

    if (newUserPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      // Crear usuario en Authentication
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newUserEmail,
        newUserPassword
      );

      // Crear documento en Firestore con el UID como ID
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: newUserEmail,
        role: newUserRole,
        createdAt: new Date()
      });

      alert('✅ Usuario creado exitosamente. Vuelve a iniciar sesión.');
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserRole('vendedor');
      setShowAddUser(false);

      // Cerrar sesión (el admin tendrá que volver a entrar)
      await auth.signOut();
    } catch (error) {
      console.error('Error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError('Este email ya está registrado');
      } else {
        setError('Error al crear usuario: ' + error.message);
      }
      setLoading(false);
    }
  };

  const deleteUser = async (userId, userEmail) => {
    if (userId === currentUser?.uid) {
      alert('❌ No puedes eliminar tu propia cuenta');
      return;
    }

    if (!window.confirm(`¿Estás seguro de eliminar a ${userEmail}?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'users', userId));
      alert('✅ Usuario eliminado de Firestore. Nota: El usuario aún existe en Authentication.');
      loadUsers();
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error al eliminar usuario');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content user-management-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>👥 Gestión de Usuarios</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="user-management-content">
          <div className="users-header">
            <h3>Usuarios Registrados ({users.length})</h3>
            <button 
              className="btn-add-user" 
              onClick={() => setShowAddUser(!showAddUser)}
            >
              {showAddUser ? '✕ Cancelar' : '➕ Nuevo Usuario'}
            </button>
          </div>

          {showAddUser && (
            <form onSubmit={createUser} className="add-user-form">
              <h4>Crear Nuevo Usuario</h4>
              
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="usuario@ejemplo.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Contraseña</label>
                <input
                  type="password"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                />
              </div>

              <div className="form-group">
                <label>Rol</label>
                <select value={newUserRole} onChange={(e) => setNewUserRole(e.target.value)}>
                  <option value="vendedor">Vendedor</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>

              {error && <div className="error-message">{error}</div>}

              <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? 'Creando...' : '✅ Crear Usuario'}
              </button>
            </form>
          )}

          <div className="users-list">
            {users.length === 0 ? (
              <div className="empty-state">
                <p>No hay usuarios registrados</p>
              </div>
            ) : (
              users.map(user => (
                <div key={user.id} className="user-card">
                  <div className="user-info">
                    <div className="user-avatar">
                      {user.role === 'admin' ? '👑' : '👤'}
                    </div>
                    <div className="user-details">
                      <h4>{user.email}</h4>
                      <span className={`role-badge ${user.role}`}>
                        {user.role === 'admin' ? 'Administrador' : 'Vendedor'}
                      </span>
                    </div>
                  </div>
                  
                  {user.id !== currentUser?.uid && (
                    <button 
                      className="btn-delete-user"
                      onClick={() => deleteUser(user.id, user.email)}
                    >
                      🗑️
                    </button>
                  )}
                  
                  {user.id === currentUser?.uid && (
                    <span className="current-user-badge">Tú</span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserManagement;